import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import VideoCard from '@/components/feature/VideoCard';
import { articles, videos } from '@/mocks/videoData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const article = useMemo(() => {
    return articles.find((a) => a.id === Number(id));
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 2);
  }, [article]);

  const relatedVideos = useMemo(() => {
    if (!article) return [];
    return videos.filter((v) => v.category === article.category).slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-article-line text-2xl"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">{t('page_not_found')}</h1>
          <p className="text-foreground-500 text-sm mb-6">{t('page_not_found_desc')}</p>
          <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
            {t('back_to_home')}
          </Link>
        </div>
      </div>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "author": {
      "@type": "Person",
      "name": article.author,
    },
    "datePublished": article.date,
    "image": article.thumbnail,
    "url": `https://www.jingpinshipin.com/article/${article.id}`,
    "publisher": {
      "@type": "Organization",
      "name": "精品视频",
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.jingpinshipin.com/article/${article.id}`}
        ogType="article"
        schema={schema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('home_breadcrumb'), href: '/' },
                { label: t('category_breadcrumb'), href: `/category/${article.category}` },
                { label: t('article_breadcrumb') },
                { label: article.title },
              ]}
            />

            <article className="mt-6">
              <div className="mb-6">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}{t('views')}
                  </span>
                </div>
              </div>

              <div className="aspect-video rounded-lg overflow-hidden mb-8">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base space-y-4">
                {article.content.split('……').map((para, i) => (
                  <p key={i}>{para}{i < article.content.split('……').length - 1 ? '……' : ''}</p>
                ))}
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
                  {t('related_articles')}
                </h2>
                <div className="flex flex-col gap-4">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} {...a} />
                  ))}
                </div>
              </section>
            )}

            {relatedVideos.length > 0 && (
              <section className="mt-10 pt-10 border-t border-background-200">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
                  {t('related_videos')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} {...v} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}